'use client';

import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink, MoveRight, X } from 'lucide-react';
import { SectionTitle } from '@/components/ui/shared/section-title';
import { SzBadge, SzSectionHeader } from '@/components/ui/skin-zero/skin-zero-ui';
import {
  explorerFor,
  fetchBlockHash,
  prizeForEachDraw,
  raffleTickets,
  runRaffle,
  sha256hex,
  type Raffle,
  type RaffleDraw,
  type RaffleSnapshot,
} from '@/lib/raffle/draw';

/**
 * A holder raffle, shown in its collection's "about the series" section. The record comes
 * from the CMS (collection `raffles`), and everything is checked again in the visitor's
 * browser: the holder list is hashed against the published sha256, the draw block's hash is
 * fetched from public nodes, and the draws are run again from it.
 */

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const utc = (iso: string) => iso.replace('T', ' ').replace(/(\.000)?Z$/, ' UTC');

type Check3 = 'pending' | 'ok' | 'bad';

function Chip({ children, light, href }: { children: React.ReactNode; light?: boolean; href?: string }) {
  const cls = `px-1 py-[1px] rounded-sm transition-colors border inline-flex items-center gap-1 ${
    light ? 'bg-white hover:bg-gray-100 border-gray-200' : 'bg-gray-200 hover:bg-gray-300 border-transparent'
  }`;
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  ) : (
    <span className={cls}>{children}</span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start lg:items-center flex-col lg:flex-row gap-1 lg:gap-3">
      <div className="text-gray-800 shrink-0">{label}:</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function StatusChip({ state, label }: { state: Check3; label: string }) {
  return (
    <Chip light={state !== 'ok'}>
      {state === 'ok' ? <Check className="h-3 w-3" /> : state === 'bad' ? <X className="h-3 w-3" /> : null}
      {label}
      {state === 'pending' ? ' …' : state === 'bad' ? ' does not match' : ''}
    </Chip>
  );
}

function Toggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer text-gray-500 text-[10px] inline-flex items-center gap-[5px] select-none border-[#D9D9D9] border-[1px] px-[8px] py-[2px] rounded-md uppercase"
    >
      <span>
        {open ? 'Hide' : 'Read'} {label}
      </span>
      <MoveRight className={`text-gray-500 w-2 transition-transform ${open ? '-rotate-90' : ''}`} />
    </button>
  );
}

function parseSnapshot(text?: string | null): RaffleSnapshot | null {
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as RaffleSnapshot;
    return Array.isArray(parsed.holders) ? parsed : null;
  } catch {
    return null;
  }
}

export function CollectionRaffle({
  raffle,
  anchor = 'raffle',
  appearance = 'legacy',
}: {
  raffle: Raffle;
  anchor?: string;
  appearance?: 'legacy' | 'skin-zero';
}) {
  const snapshot = useMemo(() => parseSnapshot(raffle.snapshotJson), [raffle.snapshotJson]);
  const reserved = useMemo(() => raffle.reserved ?? [], [raffle.reserved]);
  const prizes = useMemo(() => raffle.prizes ?? [], [raffle.prizes]);
  const drawPrizes = useMemo(() => prizeForEachDraw(prizes), [prizes]);
  const pool = useMemo(
    () =>
      snapshot
        ? raffleTickets(
            snapshot,
            reserved.map(r => r.wallet),
            raffle.ticketRule ?? 'token'
          )
        : [],
    [snapshot, reserved, raffle.ticketRule]
  );
  const drawn = raffle.status === 'drawn' && Boolean(raffle.drawBlockHash);
  const chain = snapshot?.chain ?? 'ethereum';
  const explorer = (path: string) => `${explorerFor(chain)}/${path}`;

  const [draws, setDraws] = useState<RaffleDraw[]>([]);
  const [listCheck, setListCheck] = useState<Check3>('pending');
  const [blockCheck, setBlockCheck] = useState<Check3>('pending');
  const [showLog, setShowLog] = useState(false);
  const [showEntrants, setShowEntrants] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (raffle.snapshotJson && raffle.snapshotSha256) {
          const sha = await sha256hex(raffle.snapshotJson);
          if (!cancelled) setListCheck(sha === raffle.snapshotSha256 ? 'ok' : 'bad');
        }
        if (!drawn) return;
        const result = await runRaffle(raffle.drawBlockHash!, pool, drawPrizes.length);
        if (cancelled) return;
        setDraws(result);
        if (raffle.drawBlock) {
          const onChain = await fetchBlockHash(chain, raffle.drawBlock);
          if (!cancelled)
            setBlockCheck(
              onChain === null ? 'pending' : onChain === raffle.drawBlockHash!.toLowerCase() ? 'ok' : 'bad'
            );
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    raffle.snapshotJson,
    raffle.snapshotSha256,
    raffle.drawBlockHash,
    raffle.drawBlock,
    drawn,
    pool,
    drawPrizes.length,
    chain,
  ]);

  const names = Object.fromEntries((snapshot?.holders ?? []).map(h => [h.address, h.ens]));
  const who = (a: string) => names[a.toLowerCase()] || names[a] || short(a);
  const reservedPrize = new Map(reserved.map(r => [r.wallet.toLowerCase(), r.prize]));
  const wonPrize = new Map(draws.map(d => [d.ticket.address, drawPrizes[d.k - 1]]));
  const entrants = [...(snapshot?.holders ?? [])].sort(
    (a, b) => b.tokens.length - a.tokens.length || (a.address < b.address ? -1 : 1)
  );
  const walletsInDraw = new Set(pool.map(t => t.address)).size;
  const downloadHolders = () => {
    if (!raffle.snapshotJson) return;
    const url = URL.createObjectURL(new Blob([raffle.snapshotJson], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'snapshot.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (appearance === 'skin-zero')
    return (
      <section
        id={anchor}
        data-template-view="skin-zero-raffle"
        data-raffle={raffle.id}
        className="space-y-6 border-t border-neutral-200 py-10 dark:border-neutral-800"
      >
        <SzSectionHeader title={raffle.title} />
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-6">
            <dl className="space-y-4 rounded-xl border border-neutral-200 p-5 text-sm dark:border-neutral-800">
              <div>
                <dt className="mb-2 text-neutral-500">Prizes</dt>
                <dd className="flex flex-wrap gap-2">
                  {reserved.map((prize, i) => (
                    <SzBadge key={`reserved-${i}`}>{prize.prize} · Reserved</SzBadge>
                  ))}
                  {prizes.map((prize, i) => (
                    <SzBadge key={i}>
                      {prize.quantity ?? 1} × {prize.title}
                    </SzBadge>
                  ))}
                </dd>
              </div>
              {snapshot && (
                <div>
                  <dt className="text-neutral-500">Entrants</dt>
                  <dd>
                    {walletsInDraw} wallets · {pool.length} tickets
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">Snapshot</dt>
                <dd>
                  {snapshot ? (
                    <a
                      href={explorer(`block/${snapshot.snapshotBlock}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Block {snapshot.snapshotBlock.toLocaleString('en')} · {utc(snapshot.snapshotTime)}
                    </a>
                  ) : (
                    'Not taken yet'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">Draw</dt>
                <dd>
                  {drawn && raffle.drawBlock ? (
                    <a
                      href={explorer(`block/${raffle.drawBlock}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Block {raffle.drawBlock.toLocaleString('en')}
                    </a>
                  ) : raffle.drawTime ? (
                    utc(raffle.drawTime)
                  ) : (
                    'Not scheduled yet'
                  )}
                </dd>
              </div>
              {snapshot && (
                <div>
                  <dt className="text-neutral-500">Verification in this browser</dt>
                  <dd>
                    Holder list:{' '}
                    {listCheck === 'ok' ? 'Verified' : listCheck === 'bad' ? 'Does not match' : 'Checking…'}
                    {drawn &&
                      ` · Block hash: ${blockCheck === 'ok' ? 'Verified' : blockCheck === 'bad' ? 'Does not match' : 'Pending'}`}
                  </dd>
                </div>
              )}
            </dl>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">Rules</h3>
              <ol className="list-decimal space-y-3 pl-5 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                <li>
                  Every wallet holding a piece at the snapshot block takes part,{' '}
                  {raffle.ticketRule === 'wallet' ? 'one ticket per wallet.' : 'one ticket per piece.'}
                </li>
                {reserved.map((entry, i) => (
                  <li key={i}>
                    {entry.note || `${who(entry.wallet)} receives ${entry.prize} without a draw.`} That wallet holds no
                    tickets.
                  </li>
                ))}
                <li>
                  The seed is the hash of the first block at or after{' '}
                  {raffle.drawTime ? utc(raffle.drawTime) : 'the draw time'}, used only once that block is final. The
                  time is published before the block exists.
                </li>
                <li>
                  Draw k picks ticket sha256(&quot;block hash:k&quot;) mod tickets left. A winner’s other tickets leave
                  the pool, so each wallet wins once.
                </li>
                {(raffle.extraRules ?? '')
                  .split('\n')
                  .filter(line => line.trim())
                  .map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
              </ol>
            </section>
            {snapshot && (
              <details className="space-y-3 rounded-xl border border-neutral-200 p-5 text-sm dark:border-neutral-800">
                <summary className="cursor-pointer font-medium">All {entrants.length} entrants</summary>
                <button
                  onClick={downloadHolders}
                  className="rounded-lg border border-neutral-300 px-3 py-2 dark:border-neutral-700"
                >
                  Download holder list
                </button>
                <ul className="max-h-80 space-y-2 overflow-auto">
                  {entrants.map(holder => (
                    <li key={holder.address} className="flex items-center justify-between gap-3">
                      <a
                        href={explorer(`address/${holder.address}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 truncate underline"
                      >
                        {who(holder.address)}
                      </a>
                      <span className="shrink-0">{holder.tokens.length} pieces</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Results</h3>
            {error && (
              <p role="alert" className="text-sm text-red-600">
                Could not check the raffle: {error}
              </p>
            )}
            <ol className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 px-5 text-sm dark:divide-neutral-800 dark:border-neutral-800">
              {reserved.map((entry, i) => (
                <li key={`reserved-${i}`} className="space-y-1 py-4">
                  <p className="font-medium">{entry.prize} · Reserved</p>
                  <a
                    href={explorer(`address/${entry.wallet}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {who(entry.wallet)}
                  </a>
                </li>
              ))}
              {draws.map(draw => (
                <li key={draw.k} className="space-y-1 py-4">
                  <p className="font-medium">
                    {draw.k}. {drawPrizes[draw.k - 1]}
                  </p>
                  <a
                    href={explorer(`address/${draw.ticket.address}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {who(draw.ticket.address)} · #{draw.ticket.tokenId}
                  </a>
                </li>
              ))}
              {!draws.length && (
                <li role="status" className="py-6 text-neutral-500">
                  {drawn
                    ? error
                      ? 'Verification could not complete.'
                      : 'Checking the published draw…'
                    : 'Results will appear after the draw is published.'}
                </li>
              )}
            </ol>
            {raffle.claimUrl && drawn && (
              <Link
                href={raffle.claimUrl}
                className="inline-flex rounded-lg bg-neutral-950 px-4 py-2 text-sm font-medium text-white dark:bg-neutral-50 dark:text-neutral-950"
              >
                {raffle.claimLabel || 'Claim your prize'}
              </Link>
            )}
            {drawn && (
              <details className="space-y-3 rounded-xl border border-neutral-200 p-5 text-sm dark:border-neutral-800">
                <summary className="cursor-pointer font-medium">Verify the draw</summary>
                <p className="break-all text-xs text-neutral-500">Seed: {raffle.drawBlockHash}</p>
                {raffle.snapshotSha256 && (
                  <p className="break-all text-xs text-neutral-500">Holder list SHA-256: {raffle.snapshotSha256}</p>
                )}
                <p className="text-sm text-neutral-500">
                  The holder list is hashed, the block hash is checked against public nodes, and every result is
                  recomputed in your browser.
                </p>
                <pre className="overflow-x-auto rounded-lg bg-neutral-100 p-4 text-xs dark:bg-neutral-900">
                  {JSON.stringify(draws, null, 2)}
                </pre>
                {snapshot && (
                  <button onClick={downloadHolders} className="underline">
                    Download holder list
                  </button>
                )}
              </details>
            )}
          </div>
        </div>
      </section>
    );

  return (
    <div id={anchor} className="content-section rounded-md mb-[12px] scroll-mt-4 text-black" data-raffle={raffle.id}>
      <div className="pt-[15px] w-full px-[15px] rounded-md">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 font-mono text-[10px] pt-2 pb-12">
          {/* Left: the raffle itself */}
          <div className="pr-0 lg:pr-16 lg:mb-8">
            <SectionTitle title={raffle.title} className="mb-5" />
            <div className="space-y-4 border border-gray-100 p-3 rounded-md uppercase mb-8">
              <Row label="Prizes">
                {reserved.map((r, i) => (
                  <React.Fragment key={r.id ?? `reserved-${i}`}>
                    <Chip>{r.prize}</Chip>
                    <Chip light>Not drawn</Chip>
                  </React.Fragment>
                ))}
                {prizes.map((p, i) => (
                  <Chip key={p.id ?? `prize-${i}`}>
                    {(p.quantity ?? 1) > 1 ? `${p.quantity} × ${p.title}` : p.title}
                  </Chip>
                ))}
                {prizes.length > 0 && <Chip light>Drawn</Chip>}
              </Row>
              {snapshot && (
                <Row label="Entrants">
                  <Chip light>{walletsInDraw} wallets</Chip>
                  <Chip light>{pool.length} tickets</Chip>
                </Row>
              )}
              <Row label="Snapshot">
                {snapshot ? (
                  <>
                    <Chip light href={explorer(`block/${snapshot.snapshotBlock}`)}>
                      Block {snapshot.snapshotBlock.toLocaleString('en')}
                    </Chip>
                    <span className="text-gray-500 normal-case">{utc(snapshot.snapshotTime)}</span>
                  </>
                ) : (
                  <Chip light>Not taken yet</Chip>
                )}
              </Row>
              <Row label="Draw">
                {drawn && raffle.drawBlock ? (
                  <>
                    <Chip light href={explorer(`block/${raffle.drawBlock}`)}>
                      Block {raffle.drawBlock.toLocaleString('en')}
                    </Chip>
                    {raffle.drawBlockTime && (
                      <span className="text-gray-500 normal-case">{utc(raffle.drawBlockTime)}</span>
                    )}
                  </>
                ) : raffle.drawTime ? (
                  <span className="text-gray-500 normal-case">{utc(raffle.drawTime)}</span>
                ) : (
                  <Chip light>Not scheduled yet</Chip>
                )}
              </Row>
              {drawn && (
                <Row label="Seed">
                  <Chip light>{short(raffle.drawBlockHash!)}</Chip>
                </Row>
              )}
              {snapshot && (
                <Row label="Checked here">
                  <StatusChip state={listCheck} label="Holder list" />
                  {drawn && <StatusChip state={blockCheck} label="Block hash" />}
                </Row>
              )}
            </div>

            <SectionTitle title="Rules" className="mb-5" />
            <ol className="space-y-3 text-gray-500 leading-relaxed list-decimal pl-4 mb-8">
              <li>
                Every wallet holding a piece at the snapshot block takes part,{' '}
                {raffle.ticketRule === 'wallet' ? 'one ticket per wallet.' : 'one ticket per piece.'}
              </li>
              {reserved.map((r, i) => (
                <li key={r.id ?? `rule-${i}`}>
                  {r.note || `${who(r.wallet)} receives ${r.prize} without a draw.`} That wallet holds no tickets.
                </li>
              ))}
              <li>
                The seed is the hash of the first block at or after{' '}
                {raffle.drawTime ? utc(raffle.drawTime) : 'the draw time'}, used only once that block is final. The time
                is published before the block exists, so nobody can know the seed, the artist included.
              </li>
              <li>
                Draw <i>k</i> picks ticket sha256(&quot;block hash:k&quot;) mod tickets left. The winner&apos;s other
                tickets then leave the pool, so each wallet wins once.
              </li>
              {(raffle.extraRules ?? '')
                .split('\n')
                .map(line => line.trim())
                .filter(Boolean)
                .map((line, i) => (
                  <li key={`extra-${i}`}>{line}</li>
                ))}
            </ol>

            {snapshot && (
              <div className="flex flex-wrap gap-2">
                <Toggle
                  open={showEntrants}
                  onClick={() => setShowEntrants(!showEntrants)}
                  label={`entrants (${snapshot.holders.length})`}
                />
              </div>
            )}
            {showEntrants && snapshot && (
              <div
                className="mt-4 border border-gray-100 rounded-md p-3 max-h-[420px] overflow-y-auto"
                style={{ scrollbarWidth: 'thin' }}
              >
                <table className="w-full">
                  <thead>
                    <tr className="text-gray-500 uppercase text-left">
                      <th className="font-normal pb-2">Wallet</th>
                      <th className="font-normal pb-2 text-right">Pieces</th>
                      <th className="font-normal pb-2 text-right hidden sm:table-cell">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entrants.map(h => {
                      const badge = reservedPrize.get(h.address.toLowerCase()) ?? wonPrize.get(h.address);
                      return (
                        <tr key={h.address} className="border-t border-gray-100">
                          <td className="py-1.5 pr-2">
                            <a
                              href={explorer(`address/${h.address}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {who(h.address)}
                            </a>
                            {badge && (
                              <span className="ml-2 px-1 rounded-sm bg-gray-800 text-white uppercase">{badge}</span>
                            )}
                          </td>
                          <td className="py-1.5 text-right">{h.tokens.length}</td>
                          <td className="py-1.5 text-right text-gray-500 hidden sm:table-cell">
                            {h.tokens.map(t => `#${t}`).join(' ')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right: results, log, verification */}
          <div>
            <SectionTitle title="Results" className="mb-5 mt-8 lg:mt-0" />
            {error && <p className="text-red-600 mb-4">Could not check the raffle: {error}</p>}
            <div className="border border-gray-100 rounded-md p-3 mb-6">
              {reserved.map((r, i) => (
                <div
                  key={r.id ?? `res-${i}`}
                  className="grid grid-cols-[48px_1fr_auto] gap-3 items-baseline py-2 border-b border-gray-100"
                >
                  <span className="text-gray-500 uppercase">—</span>
                  <span className="uppercase text-gray-800">{r.prize}</span>
                  <a
                    href={explorer(`address/${r.wallet}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {who(r.wallet)}
                  </a>
                </div>
              ))}
              {!drawn && (
                <p className="text-gray-500 py-2">
                  {raffle.drawTime
                    ? `The draw runs after ${utc(raffle.drawTime)}.`
                    : 'The draw has not been scheduled yet.'}
                </p>
              )}
              {drawn && draws.length === 0 && !error && <p className="text-gray-500 py-2">Running the draw…</p>}
              {draws.map(d => (
                <div
                  key={d.k}
                  className="grid grid-cols-[48px_1fr_auto] gap-3 items-baseline py-2 border-b border-gray-100 last:border-0"
                >
                  <span className="text-gray-500">{String(d.k).padStart(2, '0')}</span>
                  <span className="uppercase text-gray-800">{drawPrizes[d.k - 1]}</span>
                  <a
                    href={explorer(`address/${d.ticket.address}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {who(d.ticket.address)} <span className="text-gray-500">· #{d.ticket.tokenId}</span>
                  </a>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              {drawn && raffle.claimUrl && (
                <Link
                  href={raffle.claimUrl}
                  className="text-[10px] inline-flex items-center gap-[5px] border border-black bg-black text-white px-[8px] py-[2px] rounded-md uppercase hover:bg-gray-800"
                >
                  {raffle.claimLabel || 'Won? Claim your prize'} <MoveRight className="w-2" />
                </Link>
              )}
              {drawn && <Toggle open={showLog} onClick={() => setShowLog(!showLog)} label="the open log" />}
            </div>
            {showLog && drawn && (
              <ol className="mt-4 bg-gray-50 border border-gray-100 rounded-md p-3 space-y-1 text-gray-600 leading-relaxed overflow-x-auto">
                <li>
                  holder list sha256{' '}
                  {listCheck === 'ok' ? 'matches' : listCheck === 'bad' ? 'does NOT match' : 'is being checked against'}{' '}
                  {raffle.snapshotSha256}
                </li>
                {snapshot && (
                  <li>
                    {snapshot.holders.length} wallets hold {snapshot.totalSupply} pieces at block{' '}
                    {snapshot.snapshotBlock}
                  </li>
                )}
                {reserved.map((r, i) => (
                  <li key={r.id ?? `log-${i}`}>
                    {r.wallet} receives {r.prize} and is not drawn
                  </li>
                ))}
                <li>{pool.length} tickets are in the draw</li>
                <li>
                  draw block {raffle.drawBlock}, first final block at or after{' '}
                  {raffle.drawTime ? utc(raffle.drawTime) : 'the draw time'}
                </li>
                <li className="break-all">
                  seed = block hash = {raffle.drawBlockHash}
                  {blockCheck === 'ok' ? ' (confirmed by public nodes just now)' : ''}
                </li>
                {draws.map(d => (
                  <li key={d.k} className="break-all">
                    draw {d.k}: sha256(&quot;{raffle.drawBlockHash!.slice(0, 10)}…:{d.k}&quot;) = {d.hash.slice(0, 16)}…
                    mod {d.poolSize} = {d.index} → token #{d.ticket.tokenId}, {d.ticket.address}
                  </li>
                ))}
                {draws.length > 0 && (
                  <li>
                    done. {draws.length} winners, {new Set(draws.map(d => d.ticket.address)).size} different wallets.
                  </li>
                )}
              </ol>
            )}

            {snapshot && (
              <>
                <SectionTitle title="Verify it yourself" className="mb-5 mt-10" />
                <div className="space-y-4 text-gray-500 leading-relaxed">
                  {raffle.verifyInstructions ? (
                    <pre className="bg-gray-50 border border-gray-100 rounded-md p-3 overflow-x-auto text-gray-700 whitespace-pre-wrap">
                      {raffle.verifyInstructions}
                    </pre>
                  ) : (
                    <p>
                      Check any holder with ownerOf(tokenId) at the snapshot block on the block explorer, then run the
                      rules above on the holder list: its sha256 is{' '}
                      {raffle.snapshotSha256 ? short(raffle.snapshotSha256) : 'published here'}.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 uppercase">
                    <button type="button" onClick={downloadHolders} className="cursor-pointer">
                      <Chip light>Download the holder list</Chip>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The raffles an artist has chosen to show for a collection. Renders nothing when there are none. */
export default function CollectionRaffles({
  collectionId,
  appearance = 'legacy',
}: {
  collectionId: number;
  appearance?: 'legacy' | 'skin-zero';
}) {
  const [raffles, setRaffles] = useState<Raffle[]>([]);

  useEffect(() => {
    let cancelled = false;
    const query = `where[collection][equals]=${collectionId}&where[showOnCollectionPage][equals]=true&depth=0&limit=5&sort=-createdAt`;
    fetch(`/api/proxy/raffles?${query}`, { cache: 'no-store' })
      .then(res => (res.ok ? res.json() : { docs: [] }))
      .then(json => {
        if (!cancelled) setRaffles(Array.isArray(json?.docs) ? (json.docs as Raffle[]) : []);
      })
      .catch(() => {
        /* a collection without a raffle, or a CMS that has no raffles yet */
      });
    return () => {
      cancelled = true;
    };
  }, [collectionId]);

  return (
    <>
      {raffles.map((raffle, index) => (
        <CollectionRaffle
          key={raffle.id}
          raffle={raffle}
          anchor={index === 0 ? 'raffle' : `raffle-${raffle.id}`}
          appearance={appearance}
        />
      ))}
    </>
  );
}
